import MessageBanner from 'LuxKit/Banners/MessageBanner'
import Modal from 'LuxKit/Modal/Modal'
import Table from 'LuxKit/Tables/Table'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import TableHeader from 'LuxKit/Tables/TableHeader'
import TableHeaderCell from 'LuxKit/Tables/TableHeaderCell'
import TableHeaderRow from 'LuxKit/Tables/TableHeaderRow'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Colour from 'components/utils/Colour'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import { LOYALTY_BONUS_POINTS_PROMOTION_MESSAGE } from 'loyalty/constants/loyaltyBonusPoints'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import determineLoyaltyBonusPointsCampaignType, { getHighestBonusPointsCampaignType, isBonusPointsCampaignType, isStatusCreditsCampaignType } from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import getLoyaltyProductTypeIcon from 'loyalty/lib/getLoyaltyProductTypeIcon'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import productLabels from 'i18n/productLabels'
import NotAvailablePlaceholder from 'loyalty/components/NotAvailablePlaceholder'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import FormatLoyaltyPoints from '../Formatters/FormatLoyaltyPoints'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import TabButton from 'LuxKit/Tabs/TabButton'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import { useAppSelector } from 'hooks/reduxHooks'
import { nonNullable } from 'lib/array/arrayUtils'
import { recalculatePointsEarnAfterBurnPerItem } from 'loyalty/lib/pointsCalculation/recalculatePointsEarnAfterBurn'
import type { BurnAdjustedItemEarn } from 'loyalty/lib/pointsCalculation/recalculatePointsEarnAfterBurn'

type ProductType = App.LoyaltyProductType

const productTypesThatAccrueNights: Array<ProductType> = [
  // hotels
  'hotel_lpc',
  'hotel_lpp_eps',
  'hotel_flash',
  'rental',
  // tours
  'le_tour',
  'tour_bank',
  'trusted_partner_tour',
  // cruises
  'cruise_group_1',
  'cruise_group_2',
  'cruise_group_3',
]

function getItemTitle(intl: ReturnType<typeof useIntl>, productType: App.LoyaltyProductType | undefined): string {
  switch (productType) {
    case 'dom_economy':
    case 'dom_business':
    case 'int_economy':
    case 'int_business':
    case 'int_first':
    case 'int_premium_economy':
      return intl.formatMessage(productLabels.flight)
    case 'tour_bank':
    case 'le_tour':
    case 'trusted_partner_tour':
      return intl.formatMessage(productLabels.tour)
    case 'tour_optional_extra':
      return intl.formatMessage({ id: 'YIO4ldNQG-E00gCYYHsZ', defaultMessage: 'Tour optional extras', description: 'Product type label for tour optional extras in loyalty points breakdown' })
    case 'cfmr':
    case 'insurance':
      return intl.formatMessage({ id: 'q8FPplVWeGMy_0_kkLgj', defaultMessage: 'Insurance/Coverage', description: 'Product type label for insurance or coverage in loyalty points breakdown' })
    case 'cruise_group_1':
    case 'cruise_group_2':
    case 'cruise_group_3':
      return intl.formatMessage(productLabels.cruise)
    case 'experience':
      return intl.formatMessage(productLabels.experience)
    case 'car_hire':
      return intl.formatMessage(productLabels.carHire)
    case 'lux_plus':
      return intl.formatMessage(productLabels.luxPlus)
    case 'shop_tier_1':
    case 'shop_tier_2':
    case 'shop_tier_3':
    case 'shop_tier_4':
    case 'shop_tier_5':
    case 'shop_shipping':
      return intl.formatMessage(productLabels.shop)
    case 'hotel_flash':
    case 'hotel_lpc':
    case 'hotel_lpp_eps':
    case 'rental':
      return intl.formatMessage({ id: 'YS7OLBCw2KrWT50E5ZMI', defaultMessage: 'Room', description: 'Product type label for hotel rooms in loyalty points breakdown' })
    default:
      return ''
  }
}

function shouldShowNights(productType: App.LoyaltyProductType | undefined): boolean {
  if (!productType) return false
  return productTypesThatAccrueNights.includes(productType)
}

interface Props {
  itemTitle?: string
  calculationRequests?: Array<App.LoyaltyPointsEarnCalculationRequest | undefined> | App.LoyaltyPointsEarnCalculationRequest | undefined
  /** Points applied via the burn slider — used to show the same reduced earn as the summary tile. */
  pointsToApply?: number
  /** Full burn price returned by the burn API for the displayed price. */
  burnPriceInPoints?: number
}

function LoyaltyPointsEarnBreakdownModal({ itemTitle, calculationRequests, pointsToApply, burnPriceInPoints }: Props) {
  const intl = useIntl()
  const shouldHideStatusCreditsAndNights = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)
  const [activeTab, setActiveTab] = useState<'summary' | 'how-it-works'>('summary')
  const { pointsCalculations } = useLoyaltyPointsEarnCalculator(calculationRequests)
  const burnAdjustedEarnByCalculation = useMemo<Map<App.LoyaltyPointsEarnCalculation, BurnAdjustedItemEarn> | undefined>(() => {
    if (!pointsToApply || pointsToApply <= 0 || !burnPriceInPoints || burnPriceInPoints <= 0) return undefined
    const completedCalculations = nonNullable(pointsCalculations.map(calculation => calculation?.data))
    if (!completedCalculations.length) return undefined
    const adjusted = recalculatePointsEarnAfterBurnPerItem(completedCalculations, pointsToApply, burnPriceInPoints)
    return new Map(completedCalculations.map((calculation, index) => [calculation, adjusted[index]]))
  }, [pointsToApply, burnPriceInPoints, pointsCalculations])
  const itemColumnWidth: '70%' | undefined = shouldHideStatusCreditsAndNights ? '70%' : undefined
  const bonusPointsCampaigns = pointsCalculations.flatMap(calculation =>
    calculation?.data ? determineLoyaltyBonusPointsCampaignType(calculation.data) : [],
  )
  const highestCampaign = getHighestBonusPointsCampaignType(bonusPointsCampaigns)
  const bonusPointsPromotionMessage = highestCampaign && LOYALTY_BONUS_POINTS_PROMOTION_MESSAGE[highestCampaign]
  const isBonusPointsCampaign = bonusPointsCampaigns.some(campaign => isBonusPointsCampaignType(campaign))
  const isStatusCreditsCampaign = !shouldHideStatusCreditsAndNights && bonusPointsCampaigns.some(campaign => isStatusCreditsCampaignType(campaign))
  const pointsCalculationsToDisplay = useMemo(() => {
    return pointsCalculations.filter(calculation => {
      if (!calculation?.data) return false
      if (shouldHideStatusCreditsAndNights) return calculation.data.basePoints || calculation.data.bonusPoints
      return calculation.data.basePoints || calculation.data.bonusPoints || calculation.data.statusCredits || calculation.data.bonusStatusCredits
    })
  }, [shouldHideStatusCreditsAndNights, pointsCalculations])
  const hasNightsColumn = useMemo(() => {
    if (shouldHideStatusCreditsAndNights) return false
    return pointsCalculationsToDisplay.some(calculation =>
      calculation?.data?.request.nights && shouldShowNights(calculation.data.request.loyaltyProductType),
    )
  }, [shouldHideStatusCreditsAndNights, pointsCalculationsToDisplay])

  const programName = getLoyaltyProgramDisplayName()
  const modalTitle = shouldHideStatusCreditsAndNights ?
      intl.formatMessage({
        id: '_fU4xHR8roudfbkG661a',
        defaultMessage: 'Earning points',
        description: 'Modal title for points earn breakdown in the loyalty program section',
      }) :
      intl.formatMessage({
        id: 'E-nIPdrRnUlTgx52Fzu7',
        defaultMessage: 'Earning points, status credits & nights',
        description: 'Modal title for points earn breakdown in the loyalty program section',
      })
  const breakdownIntro = shouldHideStatusCreditsAndNights ?
      intl.formatMessage({
        id: 'ompUy4O0LaVFQjz92T90',
        defaultMessage: 'Below is a breakdown of the points earned for each item in your cart.',
        description: 'Intro text for points earn breakdown table in the cart view',
      }) :
      intl.formatMessage({
        id: 't-TnhBs_OL_zVofdD9mO',
        defaultMessage: 'Below is a breakdown of the points, status credits and nights earned for each item in your cart.',
        description: 'Intro text for points earn breakdown table in the cart view',
      })
  const howItWorksIntro = shouldHideStatusCreditsAndNights ?
      intl.formatMessage({
        id: 'Dzhp_OnbtK2e5PBitCb1',
        defaultMessage: 'As a member of {programName}, you can earn points on eligible bookings.',
        description: 'Intro for how loyalty earning works for the logged-in user',
      }, { programName }) :
      intl.formatMessage({
        id: '8QgwLFQKA_z8XHyKDBBw',
        defaultMessage: 'As a member of {programName}, you can earn points, status credits and nights on eligible bookings.',
        description: 'Intro for how loyalty earning works for the logged-in user',
      }, { programName })

  return <Modal
    title={modalTitle}
  >
    <Group direction="vertical" gap={16}>

      <TabButtonsGroup fit="grow">
        <TabButton
          size="medium"
          isActive={activeTab === 'summary'}
          onClick={() => setActiveTab('summary')}
        >
          {intl.formatMessage({
            id: 's891dQwTZidQAVc35IkP',
            defaultMessage: 'Summary',
            description: 'Navigation tab label for the summary view in the order details or booking page',
          })}
        </TabButton>
        <TabButton
          size="medium"
          isActive={activeTab === 'how-it-works'}
          onClick={() => setActiveTab('how-it-works')}
        >
          {intl.formatMessage({
            id: 'RAqn_qwegNOm7v_2NUqt',
            defaultMessage: 'How it works',
            description: 'Navigation tab label for how it works view in a modal dialog',
          })}
        </TabButton>
      </TabButtonsGroup>

      {activeTab === 'summary' && <>
        <BodyText variant="medium" colour="light-foreground-alt">
          {breakdownIntro}
        </BodyText>
        <Table>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell width={itemColumnWidth}>
                {intl.formatMessage({
                  id: 'lweAmaUirV19UvcICeWg',
                  defaultMessage: 'Package',
                  description: 'Table header for the package column in a modal dialog',
                })}
              </TableHeaderCell>
              <TableHeaderCell minWidth={80}>
                {intl.formatMessage({
                  id: 'BdD3aj5FOJ0z7dSHEdV6',
                  defaultMessage: 'Points',
                  description: 'Table header for points column in the loyalty program section',
                })}
              </TableHeaderCell>
              {isBonusPointsCampaign && <TableHeaderCell minWidth={92}>
                <Colour value="highlight-primary-dark">
                  {intl.formatMessage({
                    id: 'sASsYW5rISYb06gmKT7N',
                    defaultMessage: 'Bonus Points',
                    description: 'Table header for bonus points column in the loyalty program section',
                  })}
                </Colour>
              </TableHeaderCell>}
              {!shouldHideStatusCreditsAndNights && <TableHeaderCell>
                {intl.formatMessage({
                  id: 'LAtqta9k4eMZP6U0oS94',
                  defaultMessage: 'Status Credits',
                  description: 'Table header for status credits column in a modal dialog',
                })}
              </TableHeaderCell>}
              {isStatusCreditsCampaign && <TableHeaderCell minWidth={92}>
                <Colour value="highlight-primary-dark">
                  {intl.formatMessage({
                    id: 'SZQAtM7eZDUdCeeZV_RA',
                    defaultMessage: 'Bonus Status Credits',
                    description: 'Table header for bonus status credits column in a modal dialog',
                  })}
                </Colour>
              </TableHeaderCell>}
              {hasNightsColumn && <TableHeaderCell minWidth={60}>
                {intl.formatMessage({
                  id: 'VNEJr88l47MTT3w4vCWl',
                  defaultMessage: 'Nights',
                  description: 'Table header for the nights column in a modal dialog',
                })}
              </TableHeaderCell>}
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {pointsCalculationsToDisplay.map((calculation, index) => {
              const Icon = getLoyaltyProductTypeIcon(calculation?.data?.request.loyaltyProductType)
              const nights = calculation?.data?.request.nights
              const showNightsForThisRow = nights && shouldShowNights(calculation?.data?.request.loyaltyProductType)
              const adjustedEarn = calculation?.data ? burnAdjustedEarnByCalculation?.get(calculation.data) : undefined
              const basePoints = adjustedEarn?.basePoints ?? calculation?.data?.basePoints
              const bonusPoints = adjustedEarn?.bonusPoints ?? calculation?.data?.bonusPoints
              const statusCredits = adjustedEarn?.statusCredits ?? calculation?.data?.statusCredits
              const bonusStatusCredits = adjustedEarn?.bonusStatusCredits ?? calculation?.data?.bonusStatusCredits
              return <TableBodyRow key={index}>
                <TableBodyCell width={itemColumnWidth}>
                  <BodyTextBlock variant="small" startIcon={<Icon />}>
                    {calculation?.data?.request.displayTitle ?? itemTitle ?? getItemTitle(intl, calculation?.data?.request.loyaltyProductType)}
                  </BodyTextBlock>
                </TableBodyCell>
                <TableBodyCell>
                  {!!basePoints && <FormatLoyaltyPoints abbreviated points={basePoints} />}
                  {!basePoints && <NotAvailablePlaceholder />}
                </TableBodyCell>
                {isBonusPointsCampaign && <TableBodyCell>
                  {!!bonusPoints && <Colour value="highlight-primary-dark"><FormatLoyaltyPoints abbreviated points={bonusPoints} /></Colour>}
                  {!bonusPoints && <NotAvailablePlaceholder />}
                </TableBodyCell>}
                {!shouldHideStatusCreditsAndNights && <TableBodyCell>
                  {!!statusCredits && <FormatDecimal value={statusCredits} />}
                  {!statusCredits && <NotAvailablePlaceholder />}
                </TableBodyCell>}
                {isStatusCreditsCampaign && <TableBodyCell>
                  {!!bonusStatusCredits && <Colour value="highlight-primary-dark"><FormatDecimal value={bonusStatusCredits} /></Colour>}
                  {!bonusStatusCredits && <NotAvailablePlaceholder />}
                </TableBodyCell>}
                {hasNightsColumn && <TableBodyCell>
                  {showNightsForThisRow ? nights : <NotAvailablePlaceholder />}
                </TableBodyCell>}
              </TableBodyRow>
            })}
          </TableBody>
        </Table>
        {bonusPointsPromotionMessage && <MessageBanner kind="success" density="compact" icon={false}>
          {intl.formatMessage(bonusPointsPromotionMessage)}
        </MessageBanner>}
        <BodyText variant="small" colour="light-foreground-alt">
          {intl.formatMessage({
            id: 'GHf25J0vU2DxE1-8yPg1',
            defaultMessage: 'Your account will be credited the day after you complete your trip. For Shop orders, the final points balance will be credited 30-40 days after your order is confirmed. The final balance may be different from what you see here.',
            description: 'Cart review modal disclaimer for when loyalty points post after travel or Shop confirmation and that displayed amounts may differ',
          })}
        </BodyText>
        <BodyText variant="small" colour="light-foreground-alt">
          {intl.formatMessage({
            id: 'wluncQVDYtA27MMBfpy_',
            defaultMessage: 'You will not earn points on any portion or whole item that is paid for with points. Points can only be earned on the remaining dollar value. Some payment types are not eligible for earning points such as promotional codes, credits received before {programName} launched, credits received due to cancellation of a booking before {programName} launched.',
            description: 'Disclaimer about which payments are not eligible for earning loyalty points',
          }, { programName })}
        </BodyText>
      </>}

      {activeTab === 'how-it-works' && (
        <Group direction="vertical" gap={16}>
          <BodyText variant="medium">
            {howItWorksIntro}
          </BodyText>

          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: '3cyxGyuFj1JOIPvg1Tae',
              defaultMessage: '{programName} Points',
              description: 'Section heading for loyalty points in the loyalty program section',
            }, { programName })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: '6-QsHZkDKoSqQyK0_31D',
              defaultMessage: 'Earn and redeem points on hotels, tours, flights, cruises and more.',
              description: 'Description of loyalty points usage in the cruise offer or booking section',
            })}
          </BodyText>

          {!shouldHideStatusCreditsAndNights && <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'OrwG15hRPe0UuIxzmlRS',
              defaultMessage: '{programName} Status Credits & Nights',
              description: 'Section heading for status credits and nights in a modal dialog',
            }, { programName })}
          </BodyText>}
          {!shouldHideStatusCreditsAndNights && <BodyText variant="medium">
            {intl.formatMessage({
              id: 'mvFJtoTGf6t2B3uohaOg',
              defaultMessage: 'Collect status credits and nights to move up tiers and unlock rewards including room upgrades, airport transfers, lounge passes and more.',
              description: 'Description of status credits and nights benefits in a modal dialog',
            })}
          </BodyText>}

          {!shouldHideStatusCreditsAndNights && <Table>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>
                  {intl.formatMessage({
                    id: 'hl1B2WxcUUIzqBem9C6T',
                    defaultMessage: 'Tier',
                    description: 'Table header for loyalty tier column in the loyalty program section',
                  })}
                </TableHeaderCell>
                <TableHeaderCell>
                  {intl.formatMessage({
                    id: 'LAtqta9k4eMZP6U0oS94',
                    defaultMessage: 'Status Credits',
                    description: 'Table header for status credits column in a modal dialog',
                  })}
                </TableHeaderCell>
                <TableHeaderCell>
                  {intl.formatMessage({
                    id: 'VNEJr88l47MTT3w4vCWl',
                    defaultMessage: 'Nights',
                    description: 'Table header for the nights column in a modal dialog',
                  })}
                </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              <TableBodyRow>
                <TableBodyCell>
                  {intl.formatMessage({
                    id: 'VFPym9YLLTYv4MQYCqc1',
                    defaultMessage: 'Silver',
                    description: 'Display name for the Silver tier level in the loyalty program tier progression',
                  })}
                </TableBodyCell>
                <TableBodyCell>{intl.formatNumber(200)}</TableBodyCell>
                <TableBodyCell>{intl.formatNumber(10)}</TableBodyCell>
              </TableBodyRow>
              <TableBodyRow>
                <TableBodyCell>
                  {intl.formatMessage({
                    id: 'pBU5hpHoVldc60Wvjz7i',
                    defaultMessage: 'Gold',
                    description: 'Display name for the Gold tier level in the loyalty program tier progression panel',
                  })}
                </TableBodyCell>
                <TableBodyCell>{intl.formatNumber(600)}</TableBodyCell>
                <TableBodyCell>{intl.formatNumber(20)}</TableBodyCell>
              </TableBodyRow>
              <TableBodyRow>
                <TableBodyCell>
                  {intl.formatMessage({
                    id: 'EToXLQZupJev8ZWjja6o',
                    defaultMessage: 'Platinum',
                    description: 'Display name for the Platinum tier level in the loyalty program tier progression',
                  })}
                </TableBodyCell>
                <TableBodyCell>{intl.formatNumber(1000)}</TableBodyCell>
                <TableBodyCell>{intl.formatNumber(30)}</TableBodyCell>
              </TableBodyRow>
            </TableBody>
          </Table>}
        </Group>
      )}
    </Group>
  </Modal>
}

export default LoyaltyPointsEarnBreakdownModal
